import React, { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { useDispatch } from "react-redux";
import withStyles from "isomorphic-style-loader/withStyles";
import cx from "classnames";

import MessagesItem from "./MessagesItem/MessagesItem";
import { sendMessageAction } from "../../actions/thread/sendMessageAction";
import { getBooking } from "../../actions/booking/getBooking";
import { readMessageAction } from "../../actions/thread/readMessageAction";
import useQueryResponse from "../../lib/hooks/useQueryResponse";

import messages from "../../locale/messages";

import { inputDescLimit } from "../../helpers/FieldRestriction";
import getMoreThreads from './getMoreThreads.graphql'
import { getAllThreadItems as getAllThreadItemsQuery } from "../../lib/graphql";
import history from "../../history";

import sendIcon from '/public/dashboard/messageSendIcon.svg';

import s from './ChatMessages.css';

function ChatMessages({ bookingId, socket, handleCompleteSocketEmitter }) {
    const intl = useIntl()
    const dispatch = useDispatch()
    const [message, setMessage] = useState(null)
    const [errorMessageKey, setErrorMessageKey] = useState(null)
    const [isSending, setSending] = useState(false)
    const errorMessage = errorMessageKey ? intl.formatMessage(messages[errorMessageKey]) : null;

    const { data: threadItems, loading, fetchMore, refetch } = useQueryResponse({
        query: getAllThreadItemsQuery,
        variables: {
            offset: 0,
            bookingId
        },
        fetchPolicy: 'network-only',
        ssr: false
    });

    const getAllThreadItems = threadItems?.getAllThreadItems;

    const handleInputChange = (e) => {
        const userMessage = e.target.innerText.trim()
        setMessage(userMessage)
        setErrorMessageKey(message ? null : errorMessageKey)
    }

    const handleClick = async () => {
        if (!message || message.trim() == '') {
            setErrorMessageKey('required');
            return;
        } else if (message?.length > inputDescLimit) {
            setErrorMessageKey('inputDescLimitError');
            return;
        }
        setSending(true)
        await dispatch(sendMessageAction({ bookingId, message }));
        setMessage(null)
        setErrorMessageKey(null)
        const getInput = document.getElementById('inputText');
        getInput.innerHTML = "";
        setSending(false)
    }

    const loadMore = () => {
        fetchMore({
            query: getMoreThreads,
            variables: {
                bookingId,
                offset: getAllThreadItems?.threadItems?.length,
            },
            updateQuery: (previousResult, { fetchMoreResult }) => {
                if (!fetchMoreResult) { return previousResult; }
                return {
                    getAllThreadItems: {
                        ...previousResult.getAllThreadItems,
                        threadItems: [...previousResult.getAllThreadItems.threadItems, ...fetchMoreResult.getMoreThreads],
                    },
                };
            },
        });
    }

    useEffect(() => {
        if (socket && bookingId) {
            let variables = { bookingId, offset: 0 };

            const handleNewMessage = () => {
                dispatch(readMessageAction({ bookingId }));
                refetch(variables);
            };

            const handleBookingStatusUpdated = async (value) => {
                let socketValue = {};
                try {
                    socketValue = typeof value === 'string' ? JSON.parse(value) : value || {};
                } catch (e) {
                    socketValue = {};
                }
                if (socketValue?.data?.data?.bookingStatus == 'completed') {
                    handleCompleteSocketEmitter(true);
                }

                // Only refresh booking details when status is updated.
                // Do NOT navigate away from the chat page here.
                await dispatch(getBooking(bookingId));

                if (socketValue?.data?.data?.bookingStatus == 'completed') {
                    history.push({
                        pathname: `/tracking/${bookingId}`,
                        state: { from: history.location.pathname }
                    });
                }
            };

            socket.on(`newMessage-${bookingId}`, handleNewMessage);
            socket.on(`bookingStatusUpdated-${bookingId}`, handleBookingStatusUpdated);

            return () => {
                socket.off(`newMessage-${bookingId}`, handleNewMessage);
                socket.off(`bookingStatusUpdated-${bookingId}`, handleBookingStatusUpdated);
            };
        }
    }, [socket, bookingId, dispatch, refetch]);

    return (
        <>
            <div>
                <div className={cx(s.chatMessageBox, 'chatMessageBoxRtl')}>
                    <div className={s.chatMessageInputContainer}>
                        <div
                            id="inputText"
                            contentEditable="true"
                            className={cx(s.chatMessageInput, "chatMessageInputRtl")}
                            onInput={(e) => handleInputChange(e)}
                            suppressContentEditableWarning={true}
                            role="textbox"
                            aria-label="Chat message input"
                            tabindex="0"
                            aria-live="polite"
                        />
                        {(!message || message == '') && <span className={cx(s.chatTextAreaPlaceholder, 'chatTextAreaPlaceholderRtl')}>{intl.formatMessage(messages.messageLabel)}</span>}
                    </div>
                    <div onClick={!isSending ? () => handleClick() : null} className={cx(s.chatMessageSendIconContainer, 'chatMessageSendIconContainerRtl')}>
                        <img src={sendIcon} alt='sendIcon' />
                    </div>
                </div>
                {errorMessage && <span className={'commonErrorMessageTxt'}>{errorMessage}</span>}
            </div>
            {!loading && <MessagesItem getAllThreadItems={getAllThreadItems} loadMore={loadMore} />}
        </>
    )
}

export default withStyles(s)(ChatMessages);
